/**
 * ViewTransitions
 * Handles smooth page transitions using the View Transitions API
 */

import { Dom, Lazyload, Toggler, Dataloader } from '@brandocms/jupiter'
import gsap from 'gsap'
import { initAccordionAccessibility } from '../../utils/accordionAccessibility'

export default class ViewTransitions {
  constructor(app, opts = {}) {
    this.app = app
    this.opts = {
      // Selector for links to intercept
      linkSelector: 'a[href]',
      // Exclude external links and anchors
      excludeExternal: true,
      // Exclude links with specific attributes
      excludeSelector: '[data-no-transition], [download], [target="_blank"]',
      ...opts,
    }

    // Check for View Transitions API support
    this.supported = 'startViewTransition' in document

    // Flag to track if we're navigating via popstate (back/forward)
    this.isPopState = false

    // Track current pathname to detect hash-only changes
    this.lastPathname = window.location.pathname

    this.init()
  }

  init() {
    if (!this.supported) {
      console.log('View Transitions API not supported, using standard navigation')
      return
    }

    // Intercept link clicks
    this.interceptClicks()

    // Handle browser back/forward
    window.addEventListener('popstate', this.handlePopState.bind(this))

    // Listen for triangle nav navigation events
    window.addEventListener('triangle-nav-navigate', this.handleTriangleNavNavigate.bind(this))
  }

  interceptClicks() {
    document.addEventListener('click', e => {
      const link = e.target.closest(this.opts.linkSelector)

      if (!link) return

      // Check if link should be excluded
      if (link.matches(this.opts.excludeSelector)) return

      // Check if external link
      if (this.opts.excludeExternal && this.isExternalLink(link)) return

      // Check if anchor link (same page)
      if (this.isSamePageAnchor(link)) return

      // Prevent default navigation
      e.preventDefault()

      // Navigate with transition
      this.navigateTo(link.href)
    })
  }

  async navigateTo(url) {
    // Close mobile menu before navigation
    this.closeMobileMenu()

    console.log('[ViewTransitions] Navigation started:', url)

    // Start loading state
    document.documentElement.classList.remove('is-loading-done')
    document.documentElement.classList.add('is-loading')

    // Start loading bar
    this.startLoadingBar()

    // Start logo spin
    this.startLogoSpin()

    if (!this.supported) {
      // Fallback to standard navigation (loading state will persist until page unloads)
      window.location.href = url
      return
    }

    console.log('[ViewTransitions] Starting fetch')

    try {
      // Fetch page BEFORE starting view transition so loading animations are visible
      const html = await this.fetchPage(url)

      console.log('[ViewTransitions] Fetch complete, starting view transition')

      // Now start view transition for the DOM swap only
      const transition = document.startViewTransition(async () => {
        // Update DOM
        await this.updateDOM(html, url)

        // Scroll to top inside callback so it's part of the transition animation
        // (unless there's a hash anchor that should handle its own scrolling)
        const urlObj = new URL(url, window.location.origin)
        if (!urlObj.hash) {
          window.scrollTo(0, 0)
        }
      })

      // Wait for transition to complete
      await transition.finished

      console.log('[ViewTransitions] Transition finished')

      // Stop logo spin and loading bar
      this.stopLogoSpin()
      this.stopLoadingBar()

      // Clean up loading classes
      document.documentElement.classList.remove('is-loading', 'is-loading-done')

      // Clean up active state
      document.body.classList.remove('view-transitions-active')
    } catch (error) {
      console.error('View transition failed:', error)
      // Stop logo spin and loading bar
      this.stopLogoSpin()
      this.stopLoadingBar()
      // Clean up loading and active states
      document.documentElement.classList.remove('is-loading', 'is-loading-done')
      document.body.classList.remove('view-transitions-active')
      // Fallback to standard navigation on error
      window.location.href = url
    }
  }

  startLoadingBar() {
    // Create or get loading bar element
    let bar = document.getElementById('view-transition-loading-bar')
    if (!bar) {
      bar = document.createElement('div')
      bar.id = 'view-transition-loading-bar'
      document.body.appendChild(bar)
    }

    // Reset and start animation
    gsap.set(bar, { width: '0%', opacity: 1 })
    this.loadingBarTween = gsap.to(bar, {
      width: '90%',
      duration: 3,
      ease: 'power2.out',
    })
  }

  stopLoadingBar() {
    const bar = document.getElementById('view-transition-loading-bar')
    if (!bar) return

    // Kill the loading animation
    if (this.loadingBarTween) {
      this.loadingBarTween.kill()
      this.loadingBarTween = null
    }

    // Animate to 100% then fade out
    gsap.to(bar, {
      width: '100%',
      duration: 0.15,
      ease: 'power2.out',
      onComplete: () => {
        gsap.to(bar, {
          opacity: 0,
          duration: 0.25,
          onComplete: () => {
            bar.remove()
          },
        })
      },
    })
  }

  startLogoSpin() {
    // Respect reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return
    }

    const logo = document.getElementById('bbis-logo')
    if (!logo) return

    // If already spinning, just make sure flag is set - don't interrupt
    if (this.logoTween && this.logoTween.isActive()) {
      this.logoSpinning = true
      return
    }

    this.logoSpinning = true

    const doSpin = () => {
      if (!this.logoSpinning) return

      // Wind up, then release like a fidget spinner
      this.logoTween = gsap
        .timeline({
          onComplete: () => {
            if (this.logoSpinning) {
              setTimeout(doSpin, 300)
            }
          },
        })
        // Pull back (wind up)
        .to(logo, {
          rotation: '-=20',
          duration: 0.4,
          ease: 'power2.inOut',
          transformOrigin: 'center center',
          force3D: true,
        })
        // Release and spin with inertia
        .to(logo, {
          rotation: '+=380', // 1440 + 20 to compensate for pullback
          duration: 1.5,
          ease: 'power2.out',
          transformOrigin: 'center center',
          force3D: true,
        })
    }

    doSpin()
  }

  stopLogoSpin() {
    // Just set the flag - let current spin complete naturally
    this.logoSpinning = false
  }

  async fetchPage(url) {
    const response = await fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch page: ${response.status}`)
    }

    return response.text()
  }

  async updateDOM(html, url) {
    // Parse the new HTML
    const parser = new DOMParser()
    const newDoc = parser.parseFromString(html, 'text/html')

    // Update page title
    document.title = newDoc.title

    // Update body classes and attributes
    // Preserve view-transitions-active class during DOM swap
    // (otherwise the fallback navigation timeout triggers a full page reload)
    const wasViewTransitionActive = document.body.classList.contains('view-transitions-active')
    document.body.className = newDoc.body.className
    if (wasViewTransitionActive) {
      document.body.classList.add('view-transitions-active')
    }
    Array.from(newDoc.body.attributes).forEach(attr => {
      if (attr.name !== 'class') {
        document.body.setAttribute(attr.name, attr.value)
      }
    })

    // Update breadcrumbs (inside header)
    const oldBreadcrumbs = document.querySelector('.breadcrumbs')
    const newBreadcrumbs = newDoc.querySelector('.breadcrumbs')
    if (oldBreadcrumbs && newBreadcrumbs) {
      oldBreadcrumbs.innerHTML = newBreadcrumbs.innerHTML
      // Update data-hidden attribute
      if (newBreadcrumbs.hasAttribute('data-hidden')) {
        oldBreadcrumbs.setAttribute('data-hidden', '')
      } else {
        oldBreadcrumbs.removeAttribute('data-hidden')
      }
      // Update data-course-breadcrumbs attribute
      if (newBreadcrumbs.hasAttribute('data-course-breadcrumbs')) {
        oldBreadcrumbs.setAttribute('data-course-breadcrumbs', '')
      } else {
        oldBreadcrumbs.removeAttribute('data-course-breadcrumbs')
      }
    }

    // Update brand text state (data-has-breadcrumbs controls short/full display via CSS)
    const oldBrandText = document.querySelector('.brand-text')
    const newBrandText = newDoc.querySelector('.brand-text')
    if (oldBrandText && newBrandText) {
      if (newBrandText.hasAttribute('data-has-breadcrumbs')) {
        oldBrandText.setAttribute('data-has-breadcrumbs', '')
      } else {
        oldBrandText.removeAttribute('data-has-breadcrumbs')
      }
    }

    // Update main content
    const oldMain = document.querySelector('main')
    const newMain = newDoc.querySelector('main')
    if (oldMain && newMain) {
      oldMain.replaceWith(newMain.cloneNode(true))
    }

    // Update aside if it exists in the new page
    const oldAside = document.querySelector('aside')
    const newAside = newDoc.querySelector('aside')
    if (newAside) {
      if (oldAside) {
        oldAside.replaceWith(newAside.cloneNode(true))
      } else {
        // Add aside if it didn't exist before
        const mainEl = document.querySelector('main')
        if (mainEl && mainEl.parentElement) {
          mainEl.parentElement.appendChild(newAside.cloneNode(true))
        }
      }
    } else if (oldAside) {
      // Remove aside if new page doesn't have one
      oldAside.remove()
    }

    // Update footer
    const oldFooter = document.querySelector('footer')
    const newFooter = newDoc.querySelector('footer')
    if (oldFooter && newFooter) {
      oldFooter.replaceWith(newFooter.cloneNode(true))
    }

    // Update mobile nav FAB and drawer (for dimension/area pages)
    const oldFab = document.querySelector('.mobile-nav-fab')
    const newFab = newDoc.querySelector('.mobile-nav-fab')
    const oldDrawer = document.querySelector('.mobile-nav-drawer')
    const newDrawer = newDoc.querySelector('.mobile-nav-drawer')

    if (newFab) {
      if (oldFab) {
        oldFab.replaceWith(newFab.cloneNode(true))
      } else {
        // Add FAB if it didn't exist before
        document.body.appendChild(newFab.cloneNode(true))
      }
    } else if (oldFab) {
      // Remove FAB if new page doesn't have one
      oldFab.remove()
    }

    if (newDrawer) {
      if (oldDrawer) {
        oldDrawer.replaceWith(newDrawer.cloneNode(true))
      } else {
        // Add drawer if it didn't exist before
        document.body.appendChild(newDrawer.cloneNode(true))
      }
    } else if (oldDrawer) {
      // Remove drawer if new page doesn't have one
      oldDrawer.remove()
    }

    // Update meta tags in head
    this.updateMetaTags(newDoc)

    // Update URL (only push state if not navigating via back/forward)
    if (!this.isPopState) {
      window.history.pushState({}, '', url)
    } else {
      // Reset flag after handling popstate navigation
      this.isPopState = false
    }

    // Update lastPathname to track current page for hash change detection
    this.lastPathname = window.location.pathname

    // Re-initialize app components for new content
    this.reinitializeComponents()
  }

  updateMetaTags(newDoc) {
    // Update meta description, og tags, etc.
    const metaSelectors = [
      'meta[name="description"]',
      'meta[property^="og:"]',
      'meta[name="twitter:"]',
      'link[rel="canonical"]',
    ]

    metaSelectors.forEach(selector => {
      const oldMeta = document.head.querySelector(selector)
      const newMeta = newDoc.head.querySelector(selector)

      if (oldMeta && newMeta) {
        oldMeta.replaceWith(newMeta.cloneNode(true))
      } else if (newMeta) {
        document.head.appendChild(newMeta.cloneNode(true))
      } else if (oldMeta) {
        oldMeta.remove()
      }
    })
  }

  reinitializeComponents() {
    console.log('[ViewTransitions] reinitializeComponents() called at', Date.now())
    // Re-initialize Jupiter components for the new content
    const { app } = this

    // Clean up any stale triangle nav containers from teleport slots
    // (can happen if navigation occurred while menu was open)
    const hamburgerSlot = document.querySelector('[data-nav-teleport-slot="hamburger"]')
    if (hamburgerSlot) {
      const staleContainers = hamburgerSlot.querySelectorAll('.triangle-nav-container')
      staleContainers.forEach(container => container.remove())
    }

    // Extract full-width blocks early before other modules initialize
    if (app.fullWidthExtractor) {
      app.fullWidthExtractor.refresh()
    }

    // Re-initialize triangle nav widget if it exists
    if (app.triangleNav) {
      app.triangleNav.destroy()
      const TriangleNav = app.triangleNav.constructor
      app.triangleNav = new TriangleNav(app)
    }

    // Re-initialize list nav widget if it exists
    if (app.listNav) {
      app.listNav.destroy()
      const ListNav = app.listNav.constructor
      app.listNav = new ListNav(app)
    }

    // Refresh nav teleport after nav modules are re-initialized
    if (app.navTeleport) {
      app.navTeleport.refresh()
    }

    // Re-initialize annotations if they exist
    if (app.annotations) {
      app.annotations.destroy()
      const Annotations = app.annotations.constructor
      app.annotations = new Annotations(app)
    }

    // Re-initialize lazyload for new images in main/aside/footer
    if (app.lazyload) {
      // Lazyload doesn't have a destroy method, just create a new instance
      app.lazyload = new Lazyload(app, { useNativeLazyloadIfAvailable: false })
    }

    // Re-initialize togglers in the new content
    if (app.togglers) {
      // Only destroy togglers that are no longer in the DOM
      app.togglers = app.togglers.filter(toggler => {
        if (!document.contains(toggler.el)) {
          toggler.destroy && toggler.destroy()
          return false
        }
        return true
      })

      // Add new togglers from the updated content
      const togglers = Dom.all('[data-toggle]')
      togglers.forEach(toggleEl => {
        // Check if this toggle already has a toggler
        const exists = app.togglers.some(t => t.el === toggleEl)
        if (!exists) {
          app.togglers.push(new Toggler(app, toggleEl))
        }
      })

      // Re-initialize accordion accessibility for new content (WCAG 4.1.2)
      initAccordionAccessibility()
    }

    // Re-initialize dataloaders in the new content
    if (app.dataloaders) {
      // Only destroy dataloaders that are no longer in the DOM
      app.dataloaders = app.dataloaders.filter(dl => {
        if (!document.contains(dl.$el)) {
          dl.destroy && dl.destroy()
          return false
        }
        return true
      })

      // Add new dataloaders from the updated content
      const loaderEls = Dom.all('[data-loader]')
      Array.from(loaderEls).forEach(loaderEl => {
        // Check if this loader already has a dataloader
        const exists = app.dataloaders.some(dl => dl.$el === loaderEl)
        if (!exists) {
          app.dataloaders.push(
            new Dataloader(app, loaderEl, {
              onFetch: dataloader => {
                new Lazyload(app, { useNativeLazyloadIfAvailable: false }, dataloader.$canvasEl)
              },
            })
          )
        }
      })
    }

    // Note: Header and mobile menu are NOT reinitialized since their DOM stays intact

    // Re-run hash handling for tema: anchors after content update
    if (app.hashToggleHandler) {
      app.hashToggleHandler.refresh()
    }

    // Re-initialize tab handlers in the new content
    if (app.tabHandlers) {
      // Only keep tab handlers whose containers are still in the DOM
      app.tabHandlers = app.tabHandlers.filter(handler => {
        if (!document.contains(handler.container)) {
          return false
        }
        return true
      })

      // Add new tab handlers from the updated content
      const tabContainers = Dom.all('[b-tpl="tabContainer"]')
      tabContainers.forEach(container => {
        // Check if this container already has a handler
        const exists = app.tabHandlers.some(h => h.container === container)
        if (!exists) {
          // Import TabHandler dynamically
          import('../TabHandler').then(({ default: TabHandler }) => {
            app.tabHandlers.push(new TabHandler(app, container))
          })
        }
      })

      // Refresh existing tab handlers to handle hash navigation
      app.tabHandlers.forEach(handler => {
        if (handler.refresh) {
          handler.refresh()
        }
      })
    }

    // Re-initialize aside navigation in the new content
    if (app.asideNavigations) {
      // Only keep aside navigations whose containers are still in the DOM
      app.asideNavigations = app.asideNavigations.filter(nav => {
        if (!document.contains(nav.container)) {
          return false
        }
        return true
      })

      // Add new aside navigations from the updated content
      const asideNavContainers = Dom.all('[data-create-navigation]')
      asideNavContainers.forEach(container => {
        // Check if this container already has a navigation
        const exists = app.asideNavigations.some(n => n.container === container)
        if (!exists) {
          // Import AsideNavigation dynamically
          import('../AsideNavigation').then(({ default: AsideNavigation }) => {
            app.asideNavigations.push(new AsideNavigation(app, container))
          })
        }
      })
    }

    // Re-initialize video blocks in the new content
    if (app.videoBlocks) {
      // Only keep video blocks whose elements are still in the DOM
      app.videoBlocks = app.videoBlocks.filter(videoBlock => {
        if (!document.contains(videoBlock.el)) {
          return false
        }
        return true
      })

      // Add new video blocks from the updated content
      const videoBlockElements = Dom.all('[data-video-block]')
      videoBlockElements.forEach(block => {
        // Check if this block already has a VideoBlock instance
        const exists = app.videoBlocks.some(vb => vb.el === block)
        if (!exists) {
          // Import VideoBlock dynamically
          import('../VideoBlock').then(({ default: VideoBlock }) => {
            app.videoBlocks.push(new VideoBlock(app, block))
          })
        }
      })
    }

    // Re-initialize accordion expand buttons
    if (app.initAccordionExpandButtons) {
      app.initAccordionExpandButtons()
    }

    // Re-initialize footer video
    if (app.initFooterVideo) {
      app.initFooterVideo()
    }

    // Re-initialize course sidebars in the new content
    if (app.courseSidebars) {
      // Only keep sidebars whose elements are still in the DOM
      app.courseSidebars = app.courseSidebars.filter(sidebar => {
        if (!document.contains(sidebar.sidebar)) {
          return false
        }
        return true
      })

      // Add new course sidebars from the updated content
      const courseSidebarElements = Dom.all('[data-course-sidebar]')
      courseSidebarElements.forEach(sidebarEl => {
        // Check if this sidebar already has a CourseSidebar instance
        const exists = app.courseSidebars.some(cs => cs.sidebar === sidebarEl)
        if (!exists) {
          import('../CourseSidebar').then(({ default: CourseSidebar }) => {
            app.courseSidebars.push(new CourseSidebar(app, sidebarEl))
          })
        }
      })
    }

    // Re-initialize module hero toggles
    const moduleHeroes = Dom.all('[data-module-hero]')
    moduleHeroes.forEach(hero => {
      if (hero.hasAttribute('data-hero-initialized')) return
      hero.setAttribute('data-hero-initialized', '')

      const toggle = hero.querySelector('.hero-toggle')
      if (!toggle) return

      toggle.addEventListener('click', () => {
        const isCollapsed = hero.classList.toggle('is-collapsed')
        toggle.setAttribute('aria-expanded', !isCollapsed)
        toggle.setAttribute(
          'aria-label',
          isCollapsed ? 'Utvid modulinformasjon' : 'Minimer modulinformasjon'
        )
      })
    })

    // Remove unloaded class to show content
    document.body.classList.remove('unloaded')
  }

  closeMobileMenu() {
    // Immediately dispatch mobilemenu:closed so NavTeleport moves the triangle nav
    // back to aside BEFORE the DOM is replaced by view transitions
    window.dispatchEvent(new CustomEvent('mobilemenu:closed'))

    // Close mobile menu if it's open
    if (this.app.mobileMenu && this.app.mobileMenu.isOpen) {
      this.app.mobileMenu.close()
    }

    // Also remove menu classes directly as fallback
    document.body.classList.remove('open-menu')
    const menuButton = Dom.find('.menu-button')
    if (menuButton) {
      menuButton.classList.remove('is-active')
    }
    const hamburger = Dom.find('.hamburger')
    if (hamburger) {
      hamburger.classList.remove('is-active')
      // Reset hamburger text
      const hamburgerTextEl = hamburger.querySelector('span')
      if (hamburgerTextEl) {
        hamburgerTextEl.textContent = hamburger.dataset.openText || 'Meny'
      }
    }

    // Reset mobile menu background position immediately
    const mobileBg = Dom.find('.mobile-bg')
    if (mobileBg) {
      mobileBg.style.transform = 'translateX(100%)'
      mobileBg.style.display = 'none'
    }
  }

  handlePopState(_event) {
    // Handle browser back/forward buttons
    // Skip if it's just a hash change on the same page
    if (window.location.hash && this.lastPathname === window.location.pathname) {
      return
    }

    // Set flag so we don't push state again
    this.isPopState = true
    // Clear triangle nav animation state since we're navigating via history
    console.log('[ViewTransitions] handlePopState - CLEARING sessionStorage triangleNav:previousState')
    sessionStorage.removeItem('triangleNav:previousState')
    this.navigateTo(window.location.href)
  }

  handleTriangleNavNavigate(event) {
    // Handle navigation from triangle nav widget
    const { url } = event.detail

    if (url && url !== '#') {
      // Mark as active so triangle nav doesn't fallback to regular navigation
      document.body.classList.add('view-transitions-active')

      this.navigateTo(url)
    }
  }

  isExternalLink(link) {
    return link.hostname !== window.location.hostname
  }

  isSamePageAnchor(link) {
    // Check if it's a hash-only link (href starts with #)
    const href = link.getAttribute('href')
    if (href && href.startsWith('#')) {
      return true
    }

    return link.pathname === window.location.pathname && link.hash && link.hash.length > 1
  }
}
